import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import type { TFunction } from "i18next";
import { useForm } from "react-hook-form";
import AvailabilityFeedback from "./AvailabilityFeedback";
import CrossSellModal from "./CrossSellModal";
import type { TimeOption, DurationOption } from "../../utils/bookingRules";
import { getRulesForDate, hasStartTimePassed } from "../../utils/bookingRules";
import { useCalendarAvailability } from "../hooks/useCalendarAvailability";
import { isDayAvailableForBookingType, toDateString } from "../../utils/calendarAvailability";
import type { Availability, BookingOptions, BookingType, Rewards, VenueConfig } from "../../../common/jsonRpcTypes";
import type { CoreBookingDetails } from "../hooks/useBookingCreate";
import type { LoggedInUser } from "../hooks/useAccountDetection";
import { formatCurrency } from "../../../utils/countryLocaleMap";
import { renderRewardsBalanceMessage } from "./rewardsBalanceMessage";
import { isRewardsEligible } from "../../utils/rewardsEligibility";
import { blocksProgressToDetails, isEnquiryOutcome } from "../../utils/availabilityOutcome";
import CalendarDatePicker from "../inputs/calendarDatePicker";
import DropdownInput from "../inputs/dropdownInput";
import NumberInput from "../inputs/numberInput";
import AlertBanner from "../shared/AlertBanner";
import PrimaryButton from "../buttons/primaryButton";
import { CTA_FULL_WIDTH, CTA_SHAPE } from "../buttons/ctaClassNames";
import SlowRequestNotice from "../shared/SlowRequestNotice";
import VenueSelector from "./VenueSelector";
import { InfoCircle } from "../icons";
import { ChevronRight, Star } from "lucide-react";
import type { VenueSummary } from "../hooks/useVenueList";
import { useGuestCheckoutSettings } from "../context/GuestCheckoutSettingsContext";
import { useWidgetScroll } from "../context/WidgetScrollContext";
import BookingBackButton from "./BookingBackButton";
import { useInFlight } from "../hooks/useInFlight";

export interface BookingDetailsFormValues {
  bookingType: string;
  bookingDate: string;
  bookingTime: string;
  bookingCovers: string;
  bookingDuration: string;
}

type BookingDetailsStep = "venue" | "guests-date" | "type" | "time";

function getInitialWizardStep(
  initialWizardStep: BookingDetailsStep | undefined,
  isMultiVenue: boolean,
): BookingDetailsStep {
  if (initialWizardStep) return initialWizardStep;
  return isMultiVenue ? "venue" : "guests-date";
}

function getWizardSteps(hasTypeStep: boolean, showTypeFirst: boolean, isMultiVenue: boolean): BookingDetailsStep[] {
  const steps: BookingDetailsStep[] = [];
  if (isMultiVenue) steps.push("venue");
  if (showTypeFirst && hasTypeStep) steps.push("type");
  steps.push("guests-date");
  if (!showTypeFirst && hasTypeStep) steps.push("type");
  steps.push("time");
  return steps;
}

interface BookingDetailsFormProps {
  readonly venueId: string;
  readonly source: string;
  readonly initialDate?: string;
  readonly initialTime?: string;
  readonly initialDuration?: number;
  readonly initialCovers?: number;
  readonly initialType?: string;
  readonly fixedType?: string;
  /** Override the first wizard step shown (useful for tests and deep-linking). */
  readonly initialWizardStep?: "venue" | "guests-date" | "type" | "time";
  /**
   * True when the guest navigated back to this stage after reaching a later one.
   * Prefilled time/duration then wait for the guest to review (or change) them
   * rather than auto-checking availability straight into customer details.
   */
  readonly resumeFromLaterStage?: boolean;
  readonly venueConfig: VenueConfig | null;
  readonly availability: Availability | null;
  readonly bookingOptions: BookingOptions | null;
  readonly timeOptions: TimeOption[];
  readonly durationOptions: DurationOption[];
  readonly loading: boolean;
  readonly errors: Record<string, string[]> | null;
  readonly countryCode: string;
  readonly updateBooking: (details: Partial<CoreBookingDetails>) => Promise<void>;
  readonly updateCoreDetails: (details: Partial<CoreBookingDetails>) => void;
  readonly onNext: () => void;
  readonly onAccept: () => void;
  readonly onEnquiry: () => void;
  readonly onChangeDetails: () => void;
  readonly onClearValidationErrors?: () => void;
  /** Fallback booking type name from resolvedBookingType (used after cross-sell venue switch). */
  readonly resolvedBookingTypeName?: string;
  /** General error from updateBooking API failures, surfaced inside CrossSellModal. */
  readonly bookingError?: string | null;
  readonly rewards?: Rewards | null;
  readonly affiliateId?: string | null;
  /** Show the booking type selector before the date/covers step (default false). */
  readonly showTypeFirst?: boolean;
  /** Whether multiple venue IDs were provided and the venue picker should be shown. */
  readonly isMultiVenue?: boolean;
  /** Venue summaries for the multi-venue picker. */
  readonly venueList?: VenueSummary[];
  /** Whether the venue list is loading. */
  readonly venueListLoading?: boolean;
  /** Error message from the venue list fetch. */
  readonly venueListError?: string | null;
  /** Retry callback for the venue list fetch. */
  readonly onRetryVenueList?: () => void;
  /** Currently selected venue ID. */
  readonly selectedVenueId?: string;
  /** Callback when a venue is selected from the multi-venue picker. */
  readonly onVenueSelect?: (venueId: string) => void;
  /** Whether to show the venue search input. */
  readonly searchVenues?: boolean;
  /** Comma-separated booking type IDs to restrict the type selector to a subset of the venue's types. */
  readonly allowedTypes?: string;
  /** Pre-authenticated user from /createBooking, used to show their points balance. */
  readonly loggedInUser?: LoggedInUser | null;
  /** Name of the selected booking type. Concierge bookings never earn reward points. */
  readonly bookingTypeName?: string | null;
  /**
   * Guest count to preset when none was supplied via `initialCovers`, clamped into the
   * booking type's min/max people range. Widget-only; the full app leaves the field blank.
   */
  readonly presetCovers?: number;
}

/**
 * Compute the min/max people range based on booking rules for a specific date,
 * widening the range with any num_people_overrides.
 */
function getPeopleRangeForDate(
  bookingType: BookingType,
  date: string,
  source: string,
): { minPeople: number; maxPeople: number } {
  const ranges = getEligiblePartySizeRanges(bookingType, date, source);
  if (ranges.length === 0) return { minPeople: 1, maxPeople: 20 };

  return {
    minPeople: Math.min(...ranges.map(({ minPeople }) => minPeople)),
    maxPeople: Math.max(...ranges.map(({ maxPeople }) => maxPeople)),
  };
}

function getEligiblePartySizeRanges(
  bookingType: BookingType,
  date: string,
  source: string,
): Array<{ minPeople: number; maxPeople: number }> {
  const rules = getRulesForDate(bookingType, date, source);
  if (Object.keys(rules).length === 0) return [];

  const ranges: Array<{ minPeople: number; maxPeople: number }> = [];
  if (rules.booking_available !== false) {
    ranges.push({ minPeople: rules.min_people ?? 1, maxPeople: rules.max_people ?? 20 });
  }

  for (const override of bookingType.num_people_overrides ?? []) {
    const minPeople = Math.max(override.num_people_from, override.min_people ?? override.num_people_from);
    const maxPeople = Math.min(override.num_people_to, override.max_people ?? override.num_people_to);
    if ((override.booking_available ?? rules.booking_available) !== false && minPeople <= maxPeople) {
      ranges.push({ minPeople, maxPeople });
    }
  }

  return ranges;
}

function isPartySizeEligible(bookingType: BookingType, date: string, source: string, numPeople: number): boolean {
  const rules = getRulesForDate(bookingType, date, source);
  if (Object.keys(rules).length === 0) return false;

  const override = bookingType.num_people_overrides?.find(
    ({ num_people_from, num_people_to }) => numPeople >= num_people_from && numPeople <= num_people_to,
  );
  if (override) {
    return (
      (override.booking_available ?? rules.booking_available) !== false &&
      numPeople >= (override.min_people ?? override.num_people_from) &&
      numPeople <= (override.max_people ?? override.num_people_to)
    );
  }

  return (
    rules.booking_available !== false && numPeople >= (rules.min_people ?? 1) && numPeople <= (rules.max_people ?? 20)
  );
}

/**
 * Derive a default people range from the first booking rule when no date is selected.
 */
function getDefaultPeopleRange(bookingType: BookingType): { minPeople: number; maxPeople: number } {
  let minPeople = 1;
  let maxPeople = 20;

  const bookingRules = bookingType.booking_rules;
  if (bookingRules) {
    const firstRule = Object.values(bookingRules)[0];
    if (firstRule) {
      if (firstRule.min_people !== undefined) minPeople = firstRule.min_people;
      if (firstRule.max_people !== undefined) maxPeople = firstRule.max_people;
    }
  }

  return { minPeople, maxPeople };
}

/**
 * Build the covers (party size) dropdown options for the selected booking type.
 */
function computeCoversOptions(
  selectedBookingType: BookingType | undefined,
  watchedDate: string,
  source: string,
): Array<{ value: string; label: string }> {
  if (!selectedBookingType) return [];

  const { minPeople, maxPeople } = watchedDate
    ? getPeopleRangeForDate(selectedBookingType, watchedDate, source)
    : getDefaultPeopleRange(selectedBookingType);

  return Array.from({ length: maxPeople - minPeople + 1 }, (_, i) => {
    const val = String(minPeople + i);
    return { value: val, label: val };
  });
}

/**
 * Resolve the effective booking type: the explicitly selected one, or the venue's
 * only bookable option when there is just one (no select is rendered in that case).
 */
function deriveTypeId(selectedTypeId: string, activeBookingTypes: BookingType[]): string | undefined {
  return selectedTypeId || (activeBookingTypes.length === 1 ? activeBookingTypes[0]?.id : undefined);
}

/** The form values mirrored into the shared booking state, as last synced. */
interface SyncedFormValues {
  type: string;
  date: string;
  time: string;
  covers: string;
}

/**
 * Collect form values into a CoreBookingDetails update for the useEffect sync.
 *
 * Only fields that have genuinely changed since the last sync are included, so that a
 * change to one field can never re-assert a stale value for another. This matters when
 * the type arrives late (derived from a venue's only bookable option once venueConfig
 * loads, or after a cross-sell venue switch): the watched time may still hold the
 * pre-cross-sell value at that point, and forwarding it would clobber the correct time
 * the reducer already holds.
 */
function buildCoreDetailsUpdates(
  current: SyncedFormValues,
  previous: SyncedFormValues | null,
  activeBookingTypes: BookingType[],
): Partial<CoreBookingDetails> {
  const updates: Partial<CoreBookingDetails> = {};
  const hasChanged = (field: keyof SyncedFormValues) => !previous || previous[field] !== current[field];

  // Only sync the type if it exists in the current venue's booking types.
  // After a cross-sell venue switch the form field still holds the old
  // venue's type; blindly forwarding it would overwrite the correct type
  // already set by the reducer.
  if (hasChanged("type") && current.type && activeBookingTypes.some((bt) => bt.id === current.type)) {
    updates.type = current.type;
  }
  // A field the guest cleared is forwarded as null so the reducer drops the
  // stale value too — otherwise options (e.g. the fixed end time) keep being
  // recomputed from details already discarded. An initially-empty field is
  // skipped so an externally set value (prefill, cross-sell) is not clobbered
  // before the form catches up.
  if (hasChanged("date") && (current.date || previous?.date)) updates.date = current.date || null;
  if (hasChanged("time") && (current.time || previous?.time)) updates.time = current.time || null;
  if (hasChanged("covers") && (current.covers || previous?.covers)) {
    updates.num_people = current.covers ? Number(current.covers) : null;
  }
  return updates;
}

/**
 * Build the booking details payload from form submission data.
 */
function buildBookingDetails(
  data: BookingDetailsFormValues,
  activeBookingTypes: BookingType[],
  isUntilClose: boolean,
): Partial<CoreBookingDetails> {
  const details: Partial<CoreBookingDetails> = {
    date: data.bookingDate || undefined,
    time: data.bookingTime || undefined,
    num_people: data.bookingCovers ? Number(data.bookingCovers) : undefined,
  };

  const typeId = deriveTypeId(data.bookingType, activeBookingTypes);
  if (typeId && activeBookingTypes.some((bt) => bt.id === typeId)) {
    details.type = typeId;
  }

  if (!isUntilClose && data.bookingDuration) {
    const durationValue = Number(data.bookingDuration);
    if (durationValue > 0) {
      details.duration = durationValue;
    } else if (durationValue === -1) {
      details.duration = -1;
    }
  }

  return details;
}

/**
 * Extract the [min, max] guest count bounds from a computed covers options list.
 * Returns `null` when there are no options (or their bounds aren't numeric).
 */
function getCoversBounds(coversOptions: Array<{ value: string; label: string }>): { min: number; max: number } | null {
  if (coversOptions.length === 0) return null;

  const min = Number(coversOptions[0]?.value);
  const max = Number(coversOptions[coversOptions.length - 1]?.value);
  if (Number.isNaN(min) || Number.isNaN(max)) return null;

  return { min, max };
}

/**
 * Clamp a guest count string to the nearest bound of the given covers options,
 * returning `null` when no adjustment is needed.
 *
 * Used to re-validate an already-entered guest count after the selected date
 * changes the min/max people range for the booking type -- without this, a
 * guest count chosen for one date could silently remain invalid for another.
 */
function clampCoversToRange(
  currentValue: string,
  coversOptions: Array<{ value: string; label: string }>,
): string | null {
  if (!currentValue) return null;

  const bounds = getCoversBounds(coversOptions);
  if (!bounds) return null;

  const current = Number(currentValue);
  if (Number.isNaN(current)) return null;
  if (current < bounds.min) return String(bounds.min);
  if (current > bounds.max) return String(bounds.max);
  return null;
}

/**
 * Build a stable signature of the booking detail fields that affect availability.
 * Used to detect when the form details have changed since the last availability
 * check so a stale "enquiry"/"reject" result is not acted upon.
 */
function computeAvailabilitySignature(values: {
  type?: string;
  date?: string;
  time?: string;
  covers?: string;
  duration?: string;
}): string {
  return JSON.stringify({
    type: values.type ?? "",
    date: values.date ?? "",
    time: values.time ?? "",
    covers: values.covers ?? "",
    duration: values.duration ?? "",
  });
}

/**
 * Format duration in minutes to human-readable format (e.g., "1 hour 30 minutes").
 */
function formatDuration(minutes: number, t: TFunction): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours === 0) {
    const minuteKey = mins === 1 ? "duration_minute_one" : "duration_minute_other";
    return `${mins} ${t(minuteKey)}`;
  } else if (mins === 0) {
    const hourKey = hours === 1 ? "duration_hour_one" : "duration_hour_other";
    return `${hours} ${t(hourKey)}`;
  } else {
    const hourKey = hours === 1 ? "duration_hour_one" : "duration_hour_other";
    const minuteKey = mins === 1 ? "duration_minute_one" : "duration_minute_other";
    return `${hours} ${t(hourKey)} ${mins} ${t(minuteKey)}`;
  }
}

function useFormFieldRegistration({
  register,
  unregister,
  t,
  isUntilClose,
}: {
  register: ReturnType<typeof useForm<BookingDetailsFormValues>>["register"];
  unregister: ReturnType<typeof useForm<BookingDetailsFormValues>>["unregister"];
  t: TFunction;
  isUntilClose: boolean;
}): void {
  React.useEffect(() => {
    register("bookingDate", { required: t("field_required", "This field is required") });
  }, [register, t]);

  React.useEffect(() => {
    register("bookingCovers", { required: t("field_required", "This field is required") });
  }, [register, t]);

  React.useEffect(() => {
    if (isUntilClose) return;
    register("bookingDuration", { required: t("field_required", "This field is required") });
    return () => {
      unregister("bookingDuration");
    };
  }, [register, unregister, t, isUntilClose]);

  React.useEffect(() => {
    register("bookingType", { required: t("field_required", "This field is required") });
  }, [register, t]);
}

function useAutoSelectType({
  watchedType,
  fixedType,
  activeBookingTypes,
  setValue,
}: {
  watchedType: string;
  fixedType: string | undefined;
  activeBookingTypes: BookingType[];
  setValue: ReturnType<typeof useForm<BookingDetailsFormValues>>["setValue"];
}): void {
  React.useEffect(() => {
    if (watchedType) return;
    if (fixedType) {
      setValue("bookingType", fixedType, { shouldValidate: true });
    } else if (activeBookingTypes.length === 1 && activeBookingTypes[0]) {
      setValue("bookingType", activeBookingTypes[0].id, { shouldValidate: true });
    }
  }, [watchedType, fixedType, activeBookingTypes, setValue]);
}

function useAutoAdvanceOnAccept(availability: Availability | null | undefined, onAccept: () => void): void {
  const hasAutoAdvancedRef = useRef(false);

  useEffect(() => {
    if (availability?.action === "accept" && !hasAutoAdvancedRef.current) {
      hasAutoAdvancedRef.current = true;
      onAccept();
    }
    if (!availability) {
      hasAutoAdvancedRef.current = false;
    }
  }, [availability, onAccept]);
}

function useAutoAdvanceOnEnquire(
  availability: Availability | null | undefined,
  availabilityIsStale: boolean,
  onEnquiry: () => void,
): void {
  const hasAutoAdvancedRef = useRef(false);

  useEffect(() => {
    if (availability?.action === "enquire" && !availabilityIsStale && !hasAutoAdvancedRef.current) {
      hasAutoAdvancedRef.current = true;
      onEnquiry();
    }
    if (!availability) {
      hasAutoAdvancedRef.current = false;
    }
  }, [availability, availabilityIsStale, onEnquiry]);
}

function getStepTitle(wizardStep: string, isWidget: boolean, t: TFunction): string {
  if (wizardStep === "venue") return t("booking_select_venue_title");
  if (wizardStep === "guests-date") return t("booking_guests_date_title");
  if (wizardStep === "type") return t(isWidget ? "booking_choose_type_title_widget" : "booking_choose_type_title");
  return t(isWidget ? "booking_time_title_widget" : "booking_details_title");
}

function isAvailabilityStale(
  availability: Availability | null,
  checkedSignature: string | null,
  currentSignature: string,
  availabilityInvalidated: boolean,
): boolean {
  return (
    availability !== null &&
    (availabilityInvalidated || (checkedSignature !== null && currentSignature !== checkedSignature))
  );
}

function canProceedWithEnquiry(availability: Availability | null, availabilityIsStale: boolean): boolean {
  return !availabilityIsStale && !blocksProgressToDetails(availability) && isEnquiryOutcome(availability);
}

function shouldShowAvailabilityFeedback(availability: Availability | null, availabilityIsStale: boolean): boolean {
  return (
    availability !== null &&
    !availabilityIsStale &&
    availability.action !== "accept" &&
    availability.action !== "enquire" &&
    availability.action !== "suggest_times"
  );
}

function shouldShowCrossSellModal(availability: Availability | null, availabilityIsStale: boolean): boolean {
  return availability?.action === "suggest_times" && !availabilityIsStale;
}

function shouldShowNextButton(
  availability: Availability | null,
  availabilityIsStale: boolean,
  canProceedAsEnquiry: boolean,
  showCrossSellModal: boolean,
): boolean {
  return (
    !availability ||
    availabilityIsStale ||
    canProceedAsEnquiry ||
    (availability.action === "suggest_times" && !showCrossSellModal)
  );
}

function useCrossSellModal(
  availability: Availability | null,
): [boolean, React.Dispatch<React.SetStateAction<boolean>>] {
  const [showCrossSellModal, setShowCrossSellModal] = useState(false);

  useEffect(() => {
    setShowCrossSellModal(availability?.action === "suggest_times");
  }, [availability]);

  return [showCrossSellModal, setShowCrossSellModal];
}

function getInitialCoversValue(initialCovers: number | undefined, presetCovers: number | undefined): string {
  return initialCovers?.toString() ?? String(presetCovers ?? 2);
}

/**
 * True when the venue offers a single duration (min_duration === max_duration) and it is
 * already selected, meaning the Until dropdown is auto-completed and locked.
 */
function isDurationAutoSelected(durationOptions: DurationOption[], watchedDuration: string): boolean {
  return (
    durationOptions.length === 1 && durationOptions[0] != null && watchedDuration === String(durationOptions[0].value)
  );
}

function getNumberOfPeople(watchedCovers: string): number | undefined {
  return watchedCovers ? Number(watchedCovers) : undefined;
}

function getGuestConstraintBookingType(
  watchedType: string,
  activeBookingTypes: BookingType[],
  selectedBookingType: BookingType | undefined,
): BookingType | undefined {
  return watchedType || activeBookingTypes.length === 1 ? selectedBookingType : undefined;
}

function RewardsBanner({
  rewards,
  source,
  affiliateId,
  bookingTypeName,
  availabilityAction,
  currency,
  countryCode,
  loggedInUser,
  t,
}: Readonly<{
  rewards: Rewards | null | undefined;
  source: string;
  affiliateId: string | null | undefined;
  bookingTypeName: string | null | undefined;
  availabilityAction: string | undefined;
  currency: string;
  countryCode: string;
  loggedInUser: LoggedInUser | null | undefined;
  t: TFunction;
}>): React.ReactNode {
  if (!rewards || !isRewardsEligible({ rewards, source, affiliateId, bookingTypeName, availabilityAction })) {
    return null;
  }

  return (
    <AlertBanner
      className="gcw:mb-bs-3 gcw:bg-light-purple gcw:border-0 gcw:text-text-dmn gcw:rounded-alert"
      contentClassName="gcw:gap-4"
      icon={
        <span className="gcw:flex gcw:shrink-0 gcw:items-center gcw:justify-center gcw:w-8 gcw:h-8 gcw:rounded-full gcw:bg-dark-purple gcw:leading-none">
          <Star size={16} color="#FFFFFF" fill="#FFFFFF" aria-hidden="true" className="gcw:block" />
        </span>
      }
    >
      <div>
        <Trans
          i18nKey="booking_initial_rewards"
          defaults="You will earn <strong>{{points}} points ({{pointsValue}})</strong> by making this booking."
          values={{
            points: rewards.points,
            pointsValue: formatCurrency(rewards.pointsValue, currency, countryCode),
          }}
          components={{ strong: <strong /> }}
        />
        {renderRewardsBalanceMessage(rewards, currency, countryCode, t, !!loggedInUser, loggedInUser?.points_balance)}
      </div>
    </AlertBanner>
  );
}

/**
 * BookingDetailsForm -- Step 1 of the booking journey.
 *
 * Renders the date, time, party size, duration, and booking type
 * selection fields. Dropdowns are populated from real venue config
 * and computed time/duration options. The "Continue" button calls
 * updateBooking() with the current form values.
 */
const BookingDetailsForm: React.FC<BookingDetailsFormProps> = ({
  venueId: _venueId,
  source,
  initialDate,
  initialTime,
  initialDuration,
  initialCovers,
  initialType,
  fixedType,
  initialWizardStep,
  resumeFromLaterStage = false,
  venueConfig,
  availability,
  bookingOptions: _bookingOptions,
  timeOptions,
  durationOptions,
  loading,
  errors: _errors,
  countryCode,
  updateBooking,
  updateCoreDetails,
  onNext: _onNext,
  onAccept,
  onEnquiry,
  onChangeDetails,
  onClearValidationErrors,
  resolvedBookingTypeName,
  bookingError,
  rewards,
  affiliateId,
  showTypeFirst = false,
  isMultiVenue = false,
  venueList = [],
  venueListLoading = false,
  venueListError = null,
  onRetryVenueList,
  selectedVenueId,
  onVenueSelect,
  searchVenues = false,
  allowedTypes,
  loggedInUser,
  bookingTypeName,
  presetCovers,
}) => {
  const { t } = useTranslation();
  const { isWidget } = useGuestCheckoutSettings();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Show the cross-sell modal whenever availability returns suggest_times
  const [showCrossSellModal, setShowCrossSellModal] = useCrossSellModal(availability);
  const [timeSelectionExpired, setTimeSelectionExpired] = useState(false);

  const {
    register,
    unregister,
    handleSubmit,
    trigger,
    watch,
    setValue,
    getValues,
    formState: { errors: formErrors },
  } = useForm<BookingDetailsFormValues>({
    defaultValues: {
      bookingType: initialType ?? "",
      bookingDate: initialDate ?? "",
      bookingTime: initialTime ?? "",
      bookingCovers: getInitialCoversValue(initialCovers, presetCovers),
      bookingDuration: "",
    },
  });

  const watchedType = watch("bookingType");
  const watchedDate = watch("bookingDate");
  const watchedTime = watch("bookingTime");
  const watchedCovers = watch("bookingCovers");
  const watchedDuration = watch("bookingDuration");

  // Signature of the details associated with the latest availability response.
  // When the current form values diverge from this, the availability result is
  // considered stale: its feedback is hidden and the next "Next" click re-checks,
  // so a previously returned "enquiry" cannot be acted upon after the booking
  // type (or any other detail) is changed.
  const currentSignature = computeAvailabilitySignature({
    type: watchedType,
    date: watchedDate,
    time: watchedTime,
    covers: watchedCovers,
    duration: watchedDuration,
  });
  const attemptedSignatureRef = useRef<string | null>(null);
  const [checkedSignature, setCheckedSignature] = useState<string | null>(null);
  const [availabilityInvalidated, setAvailabilityInvalidated] = useState(false);
  useEffect(() => {
    if (!availability || !attemptedSignatureRef.current) return;
    setCheckedSignature(attemptedSignatureRef.current);
    setAvailabilityInvalidated(false);
    setPrefilledAwaitingConfirm(false);
    attemptedSignatureRef.current = null;
  }, [availability]);
  const availabilityAction = availability?.action;
  const availabilityIsStale = isAvailabilityStale(
    availability,
    attemptedSignatureRef.current ?? checkedSignature,
    currentSignature,
    availabilityInvalidated,
  );
  // An enquiry outcome the guest can act on straight away: a rejected slot or
  // one with alternative times/venues has to be resolved first.
  const canProceedAsEnquiry = canProceedWithEnquiry(availability, availabilityIsStale);

  const requiredValidation = {
    required: t("field_required", "This field is required"),
  };

  // Parse allowed type IDs from the comma-separated attribute once.
  const allowedTypeIds = useMemo(() => {
    if (!allowedTypes) return null;
    const ids = allowedTypes
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);
    return ids.length > 0 ? new Set(ids) : null;
  }, [allowedTypes]);

  // Filter out archived booking types, then restrict to allowed types if specified.
  const activeBookingTypes: BookingType[] = useMemo(() => {
    if (!venueConfig?.bookingTypes) return [];
    let types = venueConfig.bookingTypes.filter((bt) => !bt.archived);
    if (allowedTypeIds) {
      types = types.filter((bt) => allowedTypeIds.has(bt.id));
    }
    return types;
  }, [venueConfig?.bookingTypes, allowedTypeIds]);

  const hasMultipleTypes = activeBookingTypes.length > 1;
  const numPeople = getNumberOfPeople(watchedCovers);
  const selectableBookingTypes = useMemo(() => {
    if (!watchedDate || numPeople === undefined) return activeBookingTypes;

    return activeBookingTypes.filter(
      (bookingType) =>
        isPartySizeEligible(bookingType, watchedDate, source, numPeople) &&
        isDayAvailableForBookingType(watchedDate, bookingType, numPeople, source, venueConfig).available,
    );
  }, [activeBookingTypes, watchedDate, numPeople, source, venueConfig]);

  // Sync local form changes to hook state so timeOptions/durationOptions recompute reactively.
  //
  // Only dispatch when the watched values themselves have genuinely changed since the last
  // sync (tracked via a ref), rather than whenever this effect merely re-runs. `activeBookingTypes`
  // is derived from `venueConfig`, and a *new* venueConfig object is returned on every
  // /createBooking response -- including the one triggered by a cross-sell alternative
  // time/venue selection (see CrossSellModal/AvailabilityFeedback). Without this guard, this
  // effect would re-fire on that reference change and forward the form's still-stale watched
  // time back into the reducer, clobbering the correct value the cross-sell selection just set.
  const lastSyncedFormValuesRef = useRef<SyncedFormValues | null>(null);

  React.useEffect(() => {
    // Track the *derived* type (which falls back to the venue's only bookable option)
    // rather than the raw watched field. On venues with a single booking type no select is
    // rendered, so the field stays empty and the type can only be derived once venueConfig
    // has loaded -- which happens after this form first mounts. Tracking the derived value
    // lets that genuine "" -> type transition reach the reducer, while still ignoring the
    // venueConfig reference churn described above (the derived id is unchanged across
    // /createBooking responses for the same venue).
    const current: SyncedFormValues = {
      type: deriveTypeId(watchedType, activeBookingTypes) ?? "",
      date: watchedDate,
      time: watchedTime,
      covers: watchedCovers,
    };
    const previous = lastSyncedFormValuesRef.current;

    const updates = buildCoreDetailsUpdates(current, previous, activeBookingTypes);
    if (Object.keys(updates).length === 0) return;

    lastSyncedFormValuesRef.current = current;
    updateCoreDetails(updates);
  }, [watchedType, watchedDate, watchedTime, watchedCovers, activeBookingTypes, updateCoreDetails]);

  // Find the currently selected booking type object
  const selectedBookingType: BookingType | undefined = useMemo(() => {
    const typeId = deriveTypeId(watchedType, activeBookingTypes);
    if (typeId) return activeBookingTypes.find((bt) => bt.id === typeId);
    // In the step wizard the guest may not have selected a type yet (covers/date come first).
    // Use the first active type for calendar and range calculations so the earliest fields have sensible defaults.
    return activeBookingTypes[0];
  }, [watchedType, activeBookingTypes]);
  const guestConstraintBookingType = getGuestConstraintBookingType(
    watchedType,
    activeBookingTypes,
    selectedBookingType,
  );

  // Determine whether this booking type is "until close" (no duration picker needed)
  const isUntilClose = selectedBookingType?.no_duration === true;

  // Generate covers options from selected booking type rules for the current date.
  // When no date is selected yet, derive a sensible default range from the first
  // available booking rule (or fall back to 1-20) so the dropdown is never blank.
  // Computed here (rather than nearer its display usage) so it's available to the
  // guest-count "settling" logic below, ahead of the calendar availability hook.
  const coversOptions = useMemo(
    () => computeCoversOptions(guestConstraintBookingType, watchedDate, source),
    [guestConstraintBookingType, watchedDate, source],
  );

  // Only feed a "settled" guest count into the calendar-availability date-invalidation
  // check below -- i.e. one that is empty or already within the currently valid range.
  // Without this, an intermediate digit while typing a multi-digit party size (e.g. "1"
  // while entering "15" when min_people is 2) would be treated as invalid, clearing the
  // already-selected date and cascading into an unwanted re-clamp of the guest count.
  const [settledNumPeople, setSettledNumPeople] = useState<number | undefined>(numPeople);

  useEffect(() => {
    const bounds = getCoversBounds(coversOptions);
    const isSettled = numPeople === undefined || !bounds || (numPeople >= bounds.min && numPeople <= bounds.max);
    if (isSettled) {
      setSettledNumPeople(numPeople);
    }
  }, [numPeople, coversOptions]);

  const handleDateInvalidated = useCallback(() => {
    setValue("bookingDate", "");
    setValue("bookingTime", "");
    setValue("bookingDuration", "");
  }, [setValue]);

  const {
    minDateStr,
    maxDateStr,
    monthAvailability,
    viewedYear,
    viewedMonth,
    checkDayAvailability,
    goToDateMonth,
    goToNextMonth,
    goToPreviousMonth,
  } = useCalendarAvailability({
    bookingType: guestConstraintBookingType,
    numPeople: guestConstraintBookingType ? settledNumPeople : undefined,
    selectedDate: watchedDate || undefined,
    source,
    venueConfig,
    onDateInvalidated: handleDateInvalidated,
  });

  useFormFieldRegistration({ register, unregister, t, isUntilClose });
  useAutoSelectType({ watchedType, fixedType, activeBookingTypes, setValue });

  // Today / Tomorrow quick-select dates, shown as shortcut buttons above the calendar.
  const todayDateStr = useMemo(() => toDateString(new Date()), []);
  const tomorrowDateStr = useMemo(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return toDateString(tomorrow);
  }, []);

  const isQuickDateDisabled = useCallback(
    (dateStr: string) => {
      if (minDateStr && dateStr < minDateStr) return true;
      if (maxDateStr && dateStr > maxDateStr) return true;
      return !checkDayAvailability(dateStr).available;
    },
    [minDateStr, maxDateStr, checkDayAvailability],
  );

  // A fully prefilled time selection is normally checked straight away (the
  // deep-linked slot auto-advances). When the guest navigated back from a later
  // stage, though, the prefill is theirs to review: no check fires until they
  // pick a new slot or press Next, so the time step is not skipped.
  const prefilledTimeComplete = Boolean(initialTime && (isUntilClose || initialDuration));
  const timeSelectionChangedRef = useRef(prefilledTimeComplete && !resumeFromLaterStage);
  const [prefilledAwaitingConfirm, setPrefilledAwaitingConfirm] = useState(
    prefilledTimeComplete && resumeFromLaterStage,
  );

  const handleTimeChange = useCallback(() => {
    setValue("bookingDuration", "", { shouldValidate: false });
    timeSelectionChangedRef.current = true;
    setPrefilledAwaitingConfirm(false);
  }, [setValue]);

  const handleDurationChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      timeSelectionChangedRef.current = true;
      setPrefilledAwaitingConfirm(false);
      setValue("bookingDuration", e.target.value, { shouldValidate: true });
    },
    [setValue],
  );

  // Generate covers options from selected booking type rules for the current date.
  // When no date is selected yet, derive a sensible default range from the first
  // available booking rule (or fall back to 1-20) so the dropdown is never blank.
  // Re-validate the already-entered guest count whenever the covers range narrows
  // (e.g. after selecting a date whose booking rules have a stricter min/max_people).
  // Depends only on `coversOptions` (not `watchedCovers`) so this only fires when the
  // min/max bounds actually change, rather than on every keystroke while typing --
  // NumberInput already handles in-progress typing and clamps on blur/Enter.
  useEffect(() => {
    const clamped = clampCoversToRange(getValues("bookingCovers"), coversOptions);
    if (clamped !== null) {
      setValue("bookingCovers", clamped, { shouldValidate: true });
    }
  }, [coversOptions, getValues, setValue]);

  // Preset the guest count once the covers range is known, clamped into it. Skipped
  // entirely when a guest count was supplied via `initialCovers` (booking state or the
  // `num_people` query param), which always wins.
  const hasPresetCoversRef = useRef(false);

  useEffect(() => {
    if (presetCovers === undefined || initialCovers !== undefined || hasPresetCoversRef.current) return;

    const bounds = getCoversBounds(coversOptions);

    hasPresetCoversRef.current = true;
    if (getValues("bookingCovers")) return;

    const clamped = bounds ? Math.min(Math.max(presetCovers, bounds.min), bounds.max) : Math.max(presetCovers, 1);
    setValue("bookingCovers", String(clamped), { shouldValidate: true });
  }, [presetCovers, initialCovers, coversOptions, getValues, setValue]);

  // Map time options with disabled state for unavailable times
  const timeDropdownOptions = useMemo(() => {
    return timeOptions.map((opt) => ({
      value: opt.value,
      label: opt.label,
      disabled: !opt.available,
    }));
  }, [timeOptions]);

  useEffect(() => {
    if (!watchedTime) return;

    const selectedTimeStillAvailable = timeOptions.some((opt) => opt.value === watchedTime);
    if (hasStartTimePassed(watchedDate, watchedTime) && !selectedTimeStillAvailable) {
      setValue("bookingTime", "", { shouldValidate: false });
      setTimeSelectionExpired(true);
      return;
    }

    setTimeSelectionExpired(false);
  }, [timeOptions, watchedDate, watchedTime, setValue]);

  // Build "Until" dropdown options from durationOptions, mapping duration values to end-time labels.
  // The open-ended option is translated here: bookingRules has no i18n context of its own.
  const untilDropdownOptions = useMemo(() => {
    return durationOptions.map((opt) => ({
      value: String(opt.value),
      label: opt.untilClose ? t("booking_duration_until_close") : opt.label,
    }));
  }, [durationOptions, t]);

  // Show form once venueConfig has loaded
  const showForm = venueConfig !== null;

  // ── Sync initialTime into the form whenever it changes ──
  // Re-syncs (rather than a one-shot) so that an externally-driven update to
  // coreBookingDetails.time -- e.g. a guest picking an alternative hour in
  // CrossSellModal/AvailabilityFeedback, which only dispatches to the reducer
  // and never touches the RHF field directly -- is reflected back into the
  // form. Without this, the visible "time" field (and any subsequent manual
  // "Next" submission) would keep carrying the original, unavailable time.
  const lastSyncedInitialTimeRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!initialTime || timeOptions.length === 0 || lastSyncedInitialTimeRef.current === initialTime) return;

    const matchingOption = timeOptions.find((opt) => opt.value === initialTime);
    if (matchingOption) {
      setValue("bookingTime", initialTime, { shouldValidate: true });
      lastSyncedInitialTimeRef.current = initialTime;
    }
  }, [initialTime, timeOptions, setValue]);

  // ── Sync initialDuration into the form whenever it changes ──
  // Same rationale as the initialTime sync above.
  const lastSyncedInitialDurationRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!initialDuration || durationOptions.length === 0 || lastSyncedInitialDurationRef.current === initialDuration)
      return;

    const matchingOption = durationOptions.find((opt) => opt.value === initialDuration);
    if (matchingOption) {
      setValue("bookingDuration", String(initialDuration), { shouldValidate: true });
      lastSyncedInitialDurationRef.current = initialDuration;
    }
  }, [initialDuration, durationOptions, setValue]);

  // Auto-select duration when only one option exists (e.g. min_duration === max_duration).
  // Mirrors DMN's setDefaultDuration from bookController.js lines 1401-1411.
  useEffect(() => {
    if (durationOptions.length === 1 && durationOptions[0] != null) {
      setValue("bookingDuration", String(durationOptions[0].value), { shouldValidate: true });
    }
  }, [durationOptions, setValue]);

  useAutoAdvanceOnAccept(availability, onAccept);
  useAutoAdvanceOnEnquire(availability, availabilityIsStale, onEnquiry);

  /**
   * Handle form submission via the "Next" button.
   *
   * - When availability has already evaluated the current (unchanged) details as
   *   an enquiry, the feedback has been shown, so proceed to the next step as an
   *   enquiry without an additional check.
   * - Otherwise run an availability check for the current details. Starting the
   *   request invalidates the previous result; only a newly returned availability
   *   response is associated with the attempted details.
   */
  const submitGuard = useInFlight();
  const onFormSubmit = useCallback(
    async (data: BookingDetailsFormValues) => {
      if (canProceedAsEnquiry) {
        onEnquiry();
        return;
      }

      setIsSubmitting(true);
      try {
        attemptedSignatureRef.current = computeAvailabilitySignature({
          type: data.bookingType,
          date: data.bookingDate,
          time: data.bookingTime,
          covers: data.bookingCovers,
          duration: data.bookingDuration,
        });
        setAvailabilityInvalidated(true);
        const details = buildBookingDetails(data, activeBookingTypes, isUntilClose);
        await updateBooking(details);
      } finally {
        setIsSubmitting(false);
      }
    },
    [canProceedAsEnquiry, onEnquiry, activeBookingTypes, isUntilClose, updateBooking],
  );

  // Wizard sub-step state for the initial booking details panel.
  const [wizardStep, setWizardStep] = useState<BookingDetailsStep>(() =>
    getInitialWizardStep(initialWizardStep, isMultiVenue),
  );

  const hasTypeStep = hasMultipleTypes && !fixedType;

  const wizardSteps = useMemo(
    () => getWizardSteps(hasTypeStep, showTypeFirst, isMultiVenue),
    [hasTypeStep, showTypeFirst, isMultiVenue],
  );

  const scrollWidgetToScreenTop = useWidgetScroll();
  const stepHeadingRef = useRef<HTMLHeadingElement>(null);
  const previousWizardStepRef = useRef(wizardStep);
  const isAutoStartedStepRef = useRef(false);

  const hasAutoStartedTypeFirst = useRef(false);
  useEffect(() => {
    if (initialWizardStep || isMultiVenue) return;
    if (showTypeFirst && hasTypeStep && !hasAutoStartedTypeFirst.current) {
      hasAutoStartedTypeFirst.current = true;
      // The type-first step is where the journey starts rather than a screen the
      // guest navigated to, so it must not scroll the embedding page.
      isAutoStartedStepRef.current = true;
      setWizardStep("type");
    }
  }, [initialWizardStep, showTypeFirst, hasTypeStep, isMultiVenue]);

  /** Inside the widget each sub-step is a screen of its own and scrolls like a stage. */
  useEffect(() => {
    if (previousWizardStepRef.current === wizardStep) return;
    previousWizardStepRef.current = wizardStep;
    stepHeadingRef.current?.focus();
    if (isAutoStartedStepRef.current) {
      isAutoStartedStepRef.current = false;
      return;
    }
    scrollWidgetToScreenTop?.();
  }, [wizardStep, scrollWidgetToScreenTop]);

  const goToNextWizardStep = useCallback(() => {
    const idx = wizardSteps.indexOf(wizardStep);
    if (idx !== -1 && idx < wizardSteps.length - 1) {
      setWizardStep(wizardSteps[idx + 1]);
    }
  }, [wizardStep, wizardSteps]);

  const goToPreviousWizardStep = useCallback(() => {
    const idx = wizardSteps.indexOf(wizardStep);
    if (idx > 0) {
      setWizardStep(wizardSteps[idx - 1]);
    }
  }, [wizardStep, wizardSteps]);

  const canGoBack = useMemo(
    () => wizardSteps.includes(wizardStep) && wizardSteps[0] !== wizardStep,
    [wizardStep, wizardSteps],
  );

  const handleDateChange = useCallback(
    async (dateStr: string) => {
      setValue("bookingDate", dateStr, { shouldValidate: true });
      const valid = await trigger(["bookingCovers", "bookingDate"]);
      if (!valid) return;

      setValue("bookingTime", "");
      setValue("bookingDuration", "");
      onClearValidationErrors?.();
      goToNextWizardStep();
    },
    [setValue, trigger, onClearValidationErrors, goToNextWizardStep],
  );

  const handleQuickDateSelect = useCallback(
    (dateStr: string) => {
      void handleDateChange(dateStr);
      goToDateMonth(dateStr);
    },
    [handleDateChange, goToDateMonth],
  );

  useEffect(() => {
    if (
      wizardStep !== "time" ||
      !timeSelectionChangedRef.current ||
      !watchedTime ||
      (!isUntilClose && !watchedDuration)
    ) {
      return;
    }

    timeSelectionChangedRef.current = false;
    void submitGuard.run(async () => {
      const valid = await trigger(isUntilClose ? ["bookingTime"] : ["bookingTime", "bookingDuration"]);
      if (valid) await handleSubmit(onFormSubmit)();
    });
  }, [wizardStep, watchedTime, watchedDuration, isUntilClose, submitGuard, trigger, handleSubmit, onFormSubmit]);

  const goToPreviousStep = useCallback(() => {
    onClearValidationErrors?.();
    goToPreviousWizardStep();
  }, [goToPreviousWizardStep, onClearValidationErrors]);

  const selectType = useCallback(
    (typeId: string) => {
      const bookingType = activeBookingTypes.find(({ id }) => id === typeId);
      if (!bookingType) return;
      if (
        watchedDate &&
        numPeople !== undefined &&
        !isDayAvailableForBookingType(watchedDate, bookingType, numPeople, source, venueConfig).available
      ) {
        return;
      }

      onClearValidationErrors?.();
      setValue("bookingType", typeId, { shouldValidate: true });
      if (typeId !== watchedType) {
        // A different booking type has its own hours and duration rules, so the
        // previously picked time/end time can be invalid — force a fresh pick.
        setValue("bookingTime", "");
        setValue("bookingDuration", "");
      }
      goToNextWizardStep();
    },
    [
      activeBookingTypes,
      watchedType,
      watchedDate,
      numPeople,
      source,
      venueConfig,
      setValue,
      goToNextWizardStep,
      onClearValidationErrors,
    ],
  );

  const changeType = useCallback(() => {
    onClearValidationErrors?.();
    setWizardStep("type");
  }, [onClearValidationErrors]);

  const selectedTypeName = useMemo(() => {
    const typeId = watchedType || fixedType;
    if (!typeId) return "";
    const type = activeBookingTypes.find((bt) => bt.id === typeId);
    return type?.name ?? resolvedBookingTypeName ?? "";
  }, [watchedType, fixedType, activeBookingTypes, resolvedBookingTypeName]);

  if (!showForm) {
    return (
      <section aria-labelledby="booking-details-heading">
        <h2
          id="booking-details-heading"
          className="gcw:text-2xl gcw:font-heading gcw:font-bold gcw:text-text-primary gcw:mb-bs-3"
        >
          {t("booking_details_title")}
        </h2>
        <div className="gcw:flex gcw:justify-center gcw:py-bs-5">
          <div className="loading-spinner" />
        </div>
      </section>
    );
  }

  const isLoading = loading || isSubmitting || submitGuard.isPending;

  const stepTitle = getStepTitle(wizardStep, isWidget, t);
  const showAvailabilityFeedback = shouldShowAvailabilityFeedback(availability, availabilityIsStale);
  const showCrossSell = shouldShowCrossSellModal(availability, availabilityIsStale);
  const showNextButton = shouldShowNextButton(
    availability,
    availabilityIsStale,
    canProceedAsEnquiry,
    showCrossSellModal,
  );

  return (
    <section aria-labelledby="booking-details-heading" className="gcw:relative">
      {/* Loading overlay — keeps form mounted and preserves field values */}
      {isLoading && (
        <div
          className="gcw:absolute gcw:inset-0 gcw:z-10 gcw:flex gcw:items-center gcw:justify-center gcw:bg-white/60"
          aria-hidden="true"
        >
          <div className="loading-spinner" />
        </div>
      )}

      <h2
        ref={stepHeadingRef}
        id="booking-details-heading"
        tabIndex={-1}
        className="gcw:text-xl gcw:font-heading gcw:font-bold gcw:text-text-primary gcw:mb-bs-3 gcw:text-center gcw:tracking-widest"
      >
        {stepTitle}
      </h2>

      {/* Rewards alert banner — shown only when Collins would actually award the points */}
      <RewardsBanner
        rewards={rewards}
        source={source}
        affiliateId={affiliateId}
        bookingTypeName={bookingTypeName}
        availabilityAction={availability?.action}
        currency={venueConfig.currency ?? "GBP"}
        countryCode={countryCode || "GB"}
        loggedInUser={loggedInUser}
        t={t}
      />

      <form
        id="gc-booking-details-form"
        noValidate
        onSubmit={(event) => {
          // Disable before validating, so a second click or Enter cannot slip in while validation runs.
          event.preventDefault();
          void submitGuard.run(() => handleSubmit(onFormSubmit)(event));
        }}
        className="gcw:flex gcw:flex-col gcw:gap-bs-3"
        style={isLoading ? { opacity: 0.5, pointerEvents: "none" } : undefined}
        aria-busy={isLoading}
      >
        {wizardStep === "venue" && (
          <VenueSelector
            venues={venueList}
            loading={venueListLoading}
            error={venueListError}
            onRetry={onRetryVenueList}
            selectedVenueId={selectedVenueId}
            onSelect={(venueId) => {
              onClearValidationErrors?.();
              onVenueSelect?.(venueId);
              goToNextWizardStep();
            }}
            searchEnabled={searchVenues}
          />
        )}

        {wizardStep === "guests-date" && (
          <div className="gcw:flex gcw:flex-col gcw:gap-bs-3">
            {canGoBack && <BookingBackButton onClick={goToPreviousStep} t={t} />}

            {/* Party size (covers) */}
            <NumberInput
              id="booking-covers"
              name="bookingCovers"
              label={t("booking_covers")}
              min={coversOptions.length > 0 ? Number(coversOptions[0]?.value) : 1}
              max={coversOptions.length > 0 ? Number(coversOptions[coversOptions.length - 1]?.value) : undefined}
              required
              labelClassName="gcw:font-semibold gcw:text-body gcw:text-text-primary"
              value={watch("bookingCovers") || ""}
              onChange={(value) => setValue("bookingCovers", value, { shouldValidate: true })}
              error={formErrors.bookingCovers}
              bookingTypeName={
                (hasMultipleTypes && watchedType) || (!hasMultipleTypes && (initialType || fixedType))
                  ? (selectedBookingType?.name ?? "")
                  : ""
              }
              venueName={venueConfig?.title ?? ""}
            />

            {/* Today / Tomorrow quick date selection */}
            <fieldset className="gcw:flex gcw:gap-2 gcw:border-0 gcw:p-0 gcw:m-0">
              <legend className="gcw:sr-only">{t("booking_quick_date_select", "Quick date selection")}</legend>
              {[
                { dateStr: todayDateStr, label: t("booking_today", "Today") },
                { dateStr: tomorrowDateStr, label: t("booking_tomorrow", "Tomorrow") },
              ].map(({ dateStr, label }) => {
                const selected = watchedDate === dateStr;
                return (
                  <button
                    key={dateStr}
                    type="button"
                    onClick={() => handleQuickDateSelect(dateStr)}
                    disabled={isQuickDateDisabled(dateStr)}
                    aria-pressed={selected}
                    className={`booking-quick-date-button gcw:inline-flex gcw:flex-1 gcw:items-center gcw:justify-center gcw:bg-primary gcw:text-white gcw:border gcw:border-primary gcw:px-bs-3 gcw:font-medium gcw:transition-colors gcw:hover:bg-primary-hover gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 gcw:disabled:opacity-50 gcw:disabled:cursor-not-allowed ${CTA_SHAPE}`}
                  >
                    {label}
                  </button>
                );
              })}
            </fieldset>

            {/* Date picker — rendered inline so the calendar is always visible */}
            <CalendarDatePicker
              id="booking-date"
              label={t("booking_date")}
              required
              inline
              labelClassName="gcw:font-semibold gcw:text-body gcw:text-text-primary"
              value={watchedDate || ""}
              onChange={handleDateChange}
              viewedYear={viewedYear}
              viewedMonth={viewedMonth}
              onPreviousMonth={goToPreviousMonth}
              onNextMonth={goToNextMonth}
              monthAvailability={monthAvailability}
              checkDayAvailability={checkDayAvailability}
              minDate={minDateStr}
              maxDate={maxDateStr}
              error={formErrors.bookingDate}
              venueCountryCode={countryCode}
            />
          </div>
        )}

        {wizardStep === "type" && (
          <div className="gcw:flex gcw:flex-col gcw:gap-bs-3">
            {canGoBack && <BookingBackButton onClick={goToPreviousStep} t={t} />}

            <div
              id="gc-booking-type-list"
              className="gcw:flex gcw:flex-col gcw:border gcw:border-light-grey gcw:rounded-md gcw:overflow-hidden"
            >
              {selectableBookingTypes.map((type) => {
                const disabled = type.archived;
                return (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => selectType(type.id)}
                    disabled={disabled}
                    className={`gcw:flex gcw:items-center gcw:justify-between gcw:gap-2 gcw:px-4 gcw:py-3 gcw:text-left gcw:border-b gcw:border-light-grey gcw:last:border-b-0 gcw:bg-white gcw:hover:bg-lighter-grey gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-inset gcw:disabled:opacity-50 gcw:disabled:cursor-not-allowed ${
                      disabled ? "gcw:text-text-muted" : "gcw:text-text-primary"
                    }`}
                    aria-label={type.name}
                  >
                    <span className="gcw:font-medium gcw:text-body gcw:min-w-0 gcw:line-clamp-3 gcw:text-ellipsis">
                      {type.name}
                    </span>
                    {!disabled && (
                      <ChevronRight size={18} className="gcw:text-text-muted gcw:shrink-0" aria-hidden="true" />
                    )}
                  </button>
                );
              })}
            </div>

            {formErrors.bookingType && (
              <div id="booking-type-error" className="gcw:text-error gcw:text-md" role="alert">
                {formErrors.bookingType.message}
              </div>
            )}
          </div>
        )}

        {wizardStep === "time" && (
          <div className="gcw:flex gcw:flex-col gcw:gap-bs-3">
            {canGoBack && <BookingBackButton onClick={goToPreviousStep} t={t} />}

            {/* Selected booking type summary */}
            <div className="gcw:flex gcw:items-center gcw:justify-between gcw:gap-2 gcw:px-4 gcw:py-3 gcw:border gcw:border-light-grey gcw:rounded-md gcw:bg-lighter-grey">
              <span className="gcw:font-medium gcw:text-body gcw:text-text-primary gcw:min-w-0 gcw:line-clamp-3 gcw:text-ellipsis">
                {selectedTypeName}
              </span>
              {hasTypeStep && (
                <button
                  type="button"
                  onClick={changeType}
                  className="gcw:text-sm gcw:font-medium gcw:text-primary gcw:shrink-0 gcw:hover:underline gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-1 gcw:rounded-md"
                >
                  {t("booking_change", "Change")}
                </button>
              )}
            </div>

            {/* Time selector */}
            {timeSelectionExpired && (
              <AlertBanner
                variant="warning"
                icon={<InfoCircle color="#9a3412" size={16} />}
                className="info-alert widget-error-panel gcw:mb-bs-3"
              >
                <Trans
                  i18nKey="booking_time_expired_notice"
                  defaults="Your session has timed out. Please <restart>re-start your booking</restart>."
                  components={{
                    restart: (
                      <button
                        type="button"
                        onClick={() => window.location.reload()}
                        className="gcw:underline gcw:font-semibold gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-1 gcw:rounded-sm"
                      />
                    ),
                  }}
                />
              </AlertBanner>
            )}
            <DropdownInput
              id="booking-time"
              name="bookingTime"
              label={t("booking_time")}
              placeholder={
                timeDropdownOptions.length === 0 ? t("booking_no_times_available") : t("booking_select_time")
              }
              options={timeDropdownOptions}
              required
              disabled={timeDropdownOptions.length === 0}
              labelClassName="gcw:font-semibold gcw:text-body gcw:text-text-primary"
              register={register}
              onChange={handleTimeChange}
              error={formErrors.bookingTime}
              validation={requiredValidation}
            />

            {/* Until field: dropdown for fixed-duration types, static "Close" for until-close types */}
            {isUntilClose ? (
              <div className="gcw:w-full">
                <span
                  className="gcw:block gcw:mb-2 gcw:font-semibold gcw:text-body gcw:text-text-primary"
                  id="booking-until-label"
                >
                  {t("booking_until")}
                </span>
                <output
                  id="booking-until-display"
                  className="container-inputs gcw:bg-surface-secondary gcw:text-text-primary"
                  aria-labelledby="booking-until-label"
                  aria-live="polite"
                >
                  {t("booking_until_close")}
                </output>
                <p className="gcw:mt-2 gcw:text-body gcw:text-text-secondary">{t("booking_until_info_contact")}</p>
              </div>
            ) : (
              <>
                <DropdownInput
                  id="booking-duration"
                  name="bookingDuration"
                  label={t("booking_until")}
                  placeholder={t("booking_select_until")}
                  options={untilDropdownOptions}
                  required
                  disabled={
                    untilDropdownOptions.length === 0 || isDurationAutoSelected(durationOptions, watchedDuration)
                  }
                  value={watchedDuration}
                  onChange={handleDurationChange}
                  labelClassName="gcw:font-semibold gcw:text-body gcw:text-text-primary"
                  error={formErrors.bookingDuration}
                />

                {/* Info text when duration is disabled/autocompleted */}
                {isDurationAutoSelected(durationOptions, watchedDuration) && (
                  <p className="gcw:-mt-2 gcw:text-body gcw:text-text-secondary">
                    {t("booking_until_info", {
                      duration: formatDuration(durationOptions[0]?.value ?? 0, t),
                    })}
                  </p>
                )}
              </>
            )}

            {/* Availability feedback — skipped for "accept" (auto-advance) and when the
                result is stale because details changed since the last check. */}
            {showAvailabilityFeedback && availability && (
              <AvailabilityFeedback
                availability={availability}
                updateBooking={updateBooking}
                onAccept={onAccept}
                onEnquiry={onEnquiry}
                onChangeDetails={onChangeDetails}
                countryCode={countryCode}
                date={watchedDate}
                time={watchedTime}
              />
            )}

            {/* Cross-sell modal — shown when suggest_times action is returned (and not stale) */}
            {showCrossSell && availability && (
              <CrossSellModal
                show={showCrossSellModal}
                availability={availability}
                venueName={venueConfig?.title ?? ""}
                bookingDate={watchedDate}
                bookingTime={watchedTime}
                numPeople={watchedCovers ? Number(watchedCovers) : 0}
                bookingTypeName={selectedBookingType?.name ?? resolvedBookingTypeName ?? ""}
                countryCode={countryCode}
                updateBooking={updateBooking}
                onEnquiry={onEnquiry}
                onClose={() => setShowCrossSellModal(false)}
                error={bookingError}
              />
            )}

            {prefilledAwaitingConfirm && (
              <PrimaryButton text={t("booking_next")} className={CTA_FULL_WIDTH} disabled={isLoading} />
            )}

            {bookingError && (
              <button
                type="submit"
                className="gcw:w-full gcw:rounded-md gcw:bg-primary gcw:px-4 gcw:py-3 gcw:font-semibold gcw:text-white gcw:hover:bg-primary-dark gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 gcw:disabled:cursor-not-allowed gcw:disabled:opacity-50"
                disabled={isLoading}
              >
                {t("booking_try_again")}
              </button>
            )}

            {showNextButton && <SlowRequestNotice />}
          </div>
        )}
      </form>
    </section>
  );
};

export default BookingDetailsForm;
